import { useQuery } from '@tanstack/react-query'
import { getProcurementPurchaseRequests } from 'api'
import { Alert, AlertType } from 'components/Alert/Alert'
import { Badge } from 'components/Badge/Badge'
import { Card, CardBody } from 'components/Card/Card'
import { SmallImage } from 'components/Form/UploadedFile'
import { JustSpinner } from 'components/Loading/Loading'
import { ProgressBar } from 'components/ProgressBar/ProgressBar'
import { EmptyList } from 'components/Table/EmptyList'
import { PROCUREMENT_PRIORITY_META } from 'constants/meta'
import { useState } from 'react'
import { Box, Button, colors, ColorVariant, Flex } from 'styling/GlobalStyles'
import { BigText, MediumText, NormalText, SmallText } from 'styling/TypographyV2'
import {
  ProcurementPurchaseRequestItemResponse,
  ProcurementPurchaseRequestListResponse,
  ProcurementRequestClientStatus,
} from 'types/procurement'
import { toCsv } from 'utils/helpers'
import { DoubleBox } from './Components/DoubleBox'
import { MapToSkuModal } from './Components/MapToSkuModal'

export const PrePO = () => {
  const { data: requests, isLoading } = useQuery({
    queryKey: ['procurement-purchase-requests-prepo'],
    queryFn: () =>
      getProcurementPurchaseRequests({
        page: 1,
        limit: 100,
        clientStatuses: toCsv([ProcurementRequestClientStatus.Resolve]),
      }),
  })
  const [activeRequest, setActiveRequest] = useState<ProcurementPurchaseRequestListResponse | null>(
    null,
  )
  return (
    <>
      <Alert
        title='Winning Offer Required'
        description='A winning offer must be selected before budget approval can proceed. Select the best offer based on price, delivery time, and terms.'
        type={AlertType.SUCCESS}
        colorVariant={ColorVariant.PRIMARY}
        dismissKey='prepo-alert'
      />
      <DoubleBox>
        <Box style={{ overflow: 'auto' }}>
          <NormalText>File needed resolution</NormalText>
          <Flex column mt='16' gap='0.5rem'>
            {isLoading ? (
              <Flex center justcenter height='400px'>
                <JustSpinner />
              </Flex>
            ) : requests?.data.length > 0 ? (
              requests?.data.map((request) => (
                <PurchaseRequestCard
                  key={request.id}
                  purchaseRequest={request}
                  selected={activeRequest?.id === request.id}
                  onClick={() => setActiveRequest(request)}
                />
              ))
            ) : (
              <Flex center justcenter height='100%'>
                <EmptyList
                  title='No Purchase Requests found'
                  description='No Purchase Requests found'
                />
              </Flex>
            )}
          </Flex>
        </Box>
        <Box style={{ overflow: 'auto' }}>
          <Flex gap='0.5rem'>
            <NormalText>Unresolved line items</NormalText>
            {activeRequest && <Badge hexColor={colors.orange}>{'PR-' + activeRequest.id}</Badge>}
          </Flex>
          {activeRequest ? (
            <SelectedPurchaseRequest
              purchaseRequest={activeRequest}
              setPurchaseRequest={setActiveRequest}
            />
          ) : (
            <Flex center justcenter>
              <EmptyList
                maxHeight='400px'
                title='No Purchase Request selected'
                description='Select a Purchase Request to view and manage offers'
              />
            </Flex>
          )}
        </Box>
      </DoubleBox>
    </>
  )
}

const PurchaseRequestCard = ({
  purchaseRequest,
  selected,
  onClick,
}: {
  purchaseRequest: ProcurementPurchaseRequestListResponse
  selected: boolean
  onClick: () => void
}) => {
  const unresolvedItems = purchaseRequest.items.filter((item) => !item.inventoryItem)
  const totalItems = purchaseRequest.items.length
  const percentage = Math.round(((totalItems - unresolvedItems.length) / totalItems) * 100)
  const priorityMeta = PROCUREMENT_PRIORITY_META[purchaseRequest.priority]
  return (
    <Card
      padding='0.875rem'
      onClick={onClick}
      fullBorder={selected ? colors.orange : 'transparent'}
    >
      <CardBody>
        <Flex between>
          <Flex column>
            <MediumText colorVariant={ColorVariant.PRIMARY} mb='0.25rem'>
              {'#PR-' + purchaseRequest.id}
            </MediumText>
            <BigText bold mb='0.25rem'>
              {purchaseRequest.title}
            </BigText>
            <MediumText colorVariant={ColorVariant.SECONDARY}>
              {purchaseRequest.department?.name}
            </MediumText>
          </Flex>
          {priorityMeta && (
            <Badge hexColor={priorityMeta.color}>
              {PROCUREMENT_PRIORITY_META[purchaseRequest.priority].label}
            </Badge>
          )}
        </Flex>
        <Flex between center>
          <MediumText colorVariant={ColorVariant.SECONDARY}>
            {purchaseRequest.selectedQuote?.items.length} offers{' '}
          </MediumText>
        </Flex>
        <Flex between center mt='6' mb='4'>
          <SmallText colorVariant={ColorVariant.SECONDARY}>
            {totalItems - unresolvedItems.length}/{totalItems} items resolved
          </SmallText>
          <SmallText colorVariant={ColorVariant.SECONDARY}>{percentage}%</SmallText>
        </Flex>
        <ProgressBar percentage={percentage} color={colors.orange} />
      </CardBody>
    </Card>
  )
}

const SelectedPurchaseRequest = ({
  purchaseRequest,
  setPurchaseRequest,
}: {
  purchaseRequest: ProcurementPurchaseRequestListResponse
  setPurchaseRequest: (purchaseRequest: ProcurementPurchaseRequestListResponse) => void
}) => {
  const [itemToMap, setItemToMap] = useState<ProcurementPurchaseRequestItemResponse | null>(null)

  return (
    <>
      <Flex column gap='0.5rem' mt='16'>
        {purchaseRequest?.items.map((item) => (
          <SelectedPurchaseRequestCard key={item.id} item={item} setItemToMap={setItemToMap} />
        ))}
      </Flex>
      {itemToMap && (
        <MapToSkuModal
          onClose={() => setItemToMap(null)}
          requestId={purchaseRequest?.id}
          procurementPurchaseRequestItemId={itemToMap.id}
          setOrder={setPurchaseRequest}
        />
      )}
    </>
  )
}

const SelectedPurchaseRequestCard = ({
  item,
  setItemToMap,
}: {
  item: ProcurementPurchaseRequestItemResponse
  setItemToMap: (item: ProcurementPurchaseRequestItemResponse) => void
}) => {
  const priorityMeta = PROCUREMENT_PRIORITY_META[item.urgency]

  return (
    <Card padding='0.875rem' borderLeft={!item.inventoryItem ? colors.yellow : colors.green}>
      <CardBody>
        <Flex between>
          <Flex column>
            <Flex gap='0.5rem'>
              <Badge hexColor={!item.inventoryItem ? colors.yellow : colors.green}>
                {!item.inventoryItem ? 'Free-text' : `SKU-${item.inventoryItem?.sku}`}
              </Badge>
              {priorityMeta && (
                <Badge hexColor={PROCUREMENT_PRIORITY_META[item.urgency].color}>
                  {PROCUREMENT_PRIORITY_META[item.urgency].label}
                </Badge>
              )}
            </Flex>
            <BigText semibold mt='4px'>
              {item?.name}
            </BigText>
            <Flex between>
              <SmallText colorVariant={ColorVariant.SECONDARY}>
                {item.quantity} {item.unit?.name}
              </SmallText>
            </Flex>
            {item.images.length > 0 && (
              <Flex gap='0.5rem' mt='12'>
                {item.images.map((image) => (
                  <SmallImage key={image.id} file={image.imageUrl} />
                ))}
              </Flex>
            )}
          </Flex>
          {!item.inventoryItem && (
            <Flex column gap='0.5rem'>
              <Button orange onClick={() => setItemToMap(item)}>
                Map to SKU
              </Button>
              <Button>Create new SKU</Button>
            </Flex>
          )}
        </Flex>
      </CardBody>
    </Card>
  )
}
